/* ---------- Button ---------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: var(--r-sm);
  border: 1px solid var(--line-strong);
  background: var(--surface);
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
  color: var(--ink);
}
.btn:hover:not(:disabled) {
  background: var(--hover);
  color: var(--ink);
}
.btn:disabled {
  opacity: 0.55;
}
.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.primary:hover:not(:disabled) {
  background: var(--accent-hover);
  color: var(--on-accent);
}
.primary:disabled {
  opacity: 1;
  background: var(--disabled-fill);
  border-color: var(--disabled-fill);
}
.ghost {
  border-color: transparent;
  background: transparent;
  color: var(--accent);
}
.ghost:hover:not(:disabled) {
  background: var(--accent-bg);
  color: var(--accent);
}
.danger {
  border-color: var(--danger-line);
  color: var(--danger-ink);
}
.danger:hover:not(:disabled) {
  background: var(--danger-hover);
  color: var(--danger-ink);
}
.sm {
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
}

/* ---------- Pill ---------- */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--r-xs);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: var(--chip);
  color: var(--ink-2);
}
.tone-ok {
  background: var(--ok-bg);
  color: var(--ok-ink);
}
.tone-gap {
  background: var(--gap-bg);
  color: var(--gap-ink);
}
.tone-up {
  background: var(--up-bg);
  color: var(--up-ink);
}
.tone-focus {
  background: var(--focus-bg);
  color: var(--focus-fg);
}
.tone-accent {
  background: var(--accent-bg);
  color: var(--accent-ink);
}

/* ---------- Segmented ---------- */
.segmented {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: var(--track);
  border-radius: var(--r-md);
}
.segment {
  border: 0;
  border-radius: var(--r-sm);
  background: transparent;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  color: var(--muted);
}
.segment[aria-checked='true'] {
  background: var(--surface);
  box-shadow: inset 0 0 0 1px var(--line-strong);
  color: var(--ink);
  font-weight: 600;
}
.segmentedDark {
  background: var(--header-active);
}
.segmentedDark .segment {
  color: var(--header-sub);
}
.segmentedDark .segment[aria-checked='true'] {
  background: var(--surface);
  box-shadow: none;
  color: var(--ink);
}

/* ---------- Card ---------- */
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cardHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cardHead h2,
.cardHead h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

/* ---------- Dialog ---------- */
.dialog {
  width: calc(100% - 48px);
  max-width: 520px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  border: 1px solid var(--line-strong);
  border-radius: var(--r-md);
  padding: 22px 24px;
  background: var(--surface);
  color: var(--ink);
}
.dialog[open] {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dialog::backdrop {
  background: var(--scrim-strong);
}
.dialogHead h2 {
  margin: 0;
  font-size: 19px;
  line-height: 1.35;
  font-weight: 700;
  text-wrap: pretty;
}
.dialogBody {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dialogFoot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding-top: 4px;
}
@media (max-width: 759.98px) {
  .dialog {
    width: 100%;
    max-width: none;
    margin: auto 0 0;
    border-radius: var(--r-md) var(--r-md) 0 0;
    padding: 20px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  }
}

/* ---------- States ---------- */
.pageSkeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: calc(100vh - 170px);
}
.pageSkeleton > span,
.skeletonRow > span {
  display: block;
  border-radius: var(--r-md);
  background: var(--track);
}
.skeletonHero {
  height: 140px;
}
.skeletonRow {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.skeletonRow > span {
  height: 170px;
}
.skeletonRow > span:first-child {
  flex: 3 1 340px;
}
.skeletonRow > span:last-child {
  flex: 2 1 260px;
}
.skeletonFill {
  flex: 1;
  min-height: 260px;
}
.skeleton {
  border-radius: var(--r-md);
  background: var(--track);
}

.errorPanel {
  max-width: 560px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-top: 3px solid var(--gap);
  border-radius: var(--r-md);
  padding: 26px 26px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.errorPanel h1 {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.015em;
  font-weight: 700;
}
.errorPanel p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--ink-3);
  text-wrap: pretty;
}
.errorPanel code {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--ink-2);
  background: var(--bg);
  padding: 4px 8px;
  border-radius: var(--r-xs);
}

.codeState {
  max-width: 560px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}
.stateCode {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: var(--faint);
}
.codeState h1 {
  margin: 0;
  font-size: clamp(26px, 3vw, 32px);
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-weight: 700;
  text-wrap: balance;
}
.stateButton {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: var(--r-sm);
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--on-accent);
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
}
.stateButton:hover {
  background: var(--accent-hover);
  color: var(--on-accent);
}
.errorPanel .stateButton {
  margin-top: 6px;
}

.empty {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}
.empty > p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--ink-2);
}

/* ---------- Toast ---------- */
.toasts {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 70;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: max-content;
  max-width: calc(100% - 32px);
  pointer-events: none;
}
.toast {
  background: var(--ink);
  color: var(--on-accent);
  padding: 11px 18px;
  border-radius: var(--r-sm);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
}
.toastError {
  background: var(--gap);
}
@media (max-width: 759.98px) {
  .toasts {
    bottom: 84px;
  }
}

/* ---------- Form ---------- */
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.field > span:first-child {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
}
.input {
  height: 40px;
  min-width: 0;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sm);
  background: var(--surface);
  font-size: 14px;
}
textarea.input {
  height: auto;
  min-height: 100px;
  padding: 10px 12px;
  line-height: 1.55;
  resize: vertical;
}
.input[aria-invalid='true'] {
  border-color: var(--gap);
}
.hint {
  font-size: 12px;
  color: var(--faint);
}
.fieldError {
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--danger-ink);
}
@media (max-width: 759.98px) {
  .input {
    height: 44px;
  }
}

/* ---------- Celebration toast ---------- */
.toastCelebrate {
  position: relative;
  background: var(--accent-ink-strong);
}
/* A short burst from the toast: 12 pieces fly out and fade (off under prefers-reduced-motion, global.css). */
.confetti {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
}
.confetti > span {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 1px;
  opacity: 0;
  animation: confetti 1.1s ease-out forwards;
}
@keyframes confetti {
  0% {
    opacity: 1;
    transform: translate(0, 0) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx), var(--dy)) rotate(260deg);
  }
}
.confetti > span:nth-child(1) {
  --dx: 144px;
  --dy: -24px;
  background: var(--l3);
  animation-delay: 40ms;
}
.confetti > span:nth-child(2) {
  --dx: 89px;
  --dy: 8px;
  background: var(--ok);
  animation-delay: 80ms;
}
.confetti > span:nth-child(3) {
  --dx: 72px;
  --dy: 54px;
  background: var(--focus-line);
  animation-delay: 120ms;
}
.confetti > span:nth-child(4) {
  --dx: 0px;
  --dy: 40px;
  background: var(--l5);
  animation-delay: 0ms;
}
.confetti > span:nth-child(5) {
  --dx: -72px;
  --dy: 54px;
  background: var(--up);
  animation-delay: 40ms;
}
.confetti > span:nth-child(6) {
  --dx: -89px;
  --dy: 8px;
  background: var(--l4);
  animation-delay: 80ms;
}
.confetti > span:nth-child(7) {
  --dx: -144px;
  --dy: -24px;
  background: var(--l3);
  animation-delay: 120ms;
}
.confetti > span:nth-child(8) {
  --dx: -89px;
  --dy: -56px;
  background: var(--ok);
  animation-delay: 0ms;
}
.confetti > span:nth-child(9) {
  --dx: -72px;
  --dy: -102px;
  background: var(--focus-line);
  animation-delay: 40ms;
}
.confetti > span:nth-child(10) {
  --dx: 0px;
  --dy: -88px;
  background: var(--l5);
  animation-delay: 80ms;
}
.confetti > span:nth-child(11) {
  --dx: 72px;
  --dy: -102px;
  background: var(--up);
  animation-delay: 120ms;
}
.confetti > span:nth-child(12) {
  --dx: 89px;
  --dy: -56px;
  background: var(--l4);
  animation-delay: 0ms;
}

/* The bevelled gold star (LeadStar, ADR 0018): four tones of gold, lightest towards the light, and a soft
   drop shadow so it stands off the tile. Each facet is stroked in its own tone to hide the seams. */
.leadStar {
  flex: none;
  overflow: visible;
  vertical-align: -0.15em;
  filter: drop-shadow(0 0.6px 0.5px var(--lead-star-shadow));
}
.leadStar polygon {
  stroke-width: 0.3;
  stroke-linejoin: round;
}
.leadStar polygon[data-tone='1'] {
  fill: var(--lead-star-1);
  stroke: var(--lead-star-1);
}
.leadStar polygon[data-tone='2'] {
  fill: var(--lead-star-2);
  stroke: var(--lead-star-2);
}
.leadStar polygon[data-tone='3'] {
  fill: var(--lead-star-3);
  stroke: var(--lead-star-3);
}
.leadStar polygon[data-tone='4'] {
  fill: var(--lead-star-4);
  stroke: var(--lead-star-4);
}
